import React, { useContext, useCallback } from 'react';
import PropTypes from 'prop-types';
import { I18nContext } from '@bingads-webui-react/i18n-context';
import { useDefaultStyles } from '@bingads-webui-react/with-default-styles';
import { Constants } from '@bingads-webui-theme-2018/theme-fluent';
import { useResponsiveSize } from '@bingads-webui-react/with-responsive-size';
import { Stack, ActionButton, DefaultButton } from '@fluentui/react';
import {
  useWizardService,
  getIsLastStep,
  getCurrentStepId,
  WIZARD_EVENTS,
  matchStepState,
  STEP_STATES,
} from '@bingads-webui-react/wizard';
import {
  NextButton,
  BackButton,
} from '@bingads-webui-react/wizard-ui-library';
import { PMAX_LITE_WIZARD_STEP_PATHS, useSubmitToMainBundle } from '@bingads-webui-campaign-react/pmax-lite-wizard-utils';
import { useIsInternalUser, usePilotsValue } from '@bingads-webui-campaign-react/basic-context-utils';

const getStyles = () => ({
  'footerContainer-s': {
    margin: '-8px -16px',
    '& .step-back-button, .step-next-button': {
      width: 54,
      padding: '0px 8px',
    },
    '& .set-payment-later-link': {
      '& .ms-Button-label': {
        display: 'none',
      },
    },
  },
  'footerContainer-m': {
    '& .step-back-button, .step-next-button': {
      width: 54,
      padding: '0px 8px',
    },
    '& .set-payment-later-link': {
      '& .ms-Button-label': {
        display: 'none',
      },
    },
  },
});

export const CustomFooter = ({ supportMenu: SupportMenu, hideBackButton }) => {
  const i18n = useContext(I18nContext);
  const classes = useDefaultStyles(getStyles);
  const displayMode = useResponsiveSize(Constants.ResponsiveSizeBreakpoints);
  const isSmallSize = displayMode === 's' || displayMode === 'm';
  const isInternalUser = useIsInternalUser();
  const isChinaSignup = usePilotsValue('isCNMarketExpansionEnabled');

  const submitToMainBundle = useSubmitToMainBundle();
  const { state, send } = useWizardService();
  const isLastStep = getIsLastStep(state);
  const currentStepId = getCurrentStepId(state);
  const nextButtonText = isLastStep ? i18n.getString(_TL_('Go live')) : undefined;

  const isBlocking = matchStepState(state, STEP_STATES.FETCHING)
    || matchStepState(state, STEP_STATES.LOADING)
    || matchStepState(state, STEP_STATES.SAVING);

  // In Add funds step, account and campaign should already be activated, user cannot go back
  const shouldHideBackButton = hideBackButton
    || currentStepId === PMAX_LITE_WIZARD_STEP_PATHS.ADD_FUNDS
    || currentStepId === PMAX_LITE_WIZARD_STEP_PATHS.PREVIEW_ADD_FUNDS;

  const isPaymentStep = currentStepId === PMAX_LITE_WIZARD_STEP_PATHS.PAYMENT
    || currentStepId === PMAX_LITE_WIZARD_STEP_PATHS.PREVIEW_PAYMENT;

  const onClickSkipPayment = useCallback(() => {
    send(WIZARD_EVENTS.UPDATE_WIZARD_DATA, { isSkippingPayment: true });
  }, [send]);

  return (
    <Stack horizontal horizontalAlign="space-between" verticalAlign="center" className={classes[`footerContainer-${displayMode}`]}>
      {currentStepId === PMAX_LITE_WIZARD_STEP_PATHS.CONVERSION_UPSELL ?
        <DefaultButton
          disabled={isBlocking}
          onClick={submitToMainBundle}
          className="skip-conversion-button"
        >
          {i18n.getString(_TL_('Skip'))}
        </DefaultButton> :
        <Stack horizontal tokens={{ childrenGap: isSmallSize ? 8 : 16 }}>
          {!shouldHideBackButton && <BackButton />}
          <NextButton buttonText={nextButtonText} />
        </Stack>
      }
      <Stack horizontal verticalAlign="center">
        {isPaymentStep &&
          <ActionButton
            onClick={onClickSkipPayment}
            className="set-payment-later-link"
            iconProps={{ iconName: 'ReplyMirrored' }}
            aria-label={i18n.getString(_TL_('Set up payment later'))}
            disabled={isInternalUser || isBlocking}
          >
            {i18n.getString(_TL_('Set up payment later'))}
          </ActionButton>
        }
        {!isChinaSignup && <SupportMenu />}
      </Stack>
    </Stack>
  );
};

CustomFooter.propTypes = {
  supportMenu: PropTypes.func.isRequired,
  hideBackButton: PropTypes.bool,
};

CustomFooter.defaultProps = {
  hideBackButton: false,
};
